import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { db } from '../../config/firebaseConfig';
import { useTranslation } from '../../i18n/LanguageContext';
import usePagedList from '../../hooks/usePagedList';
import { CUSTOMER_COLUMNS, DEFAULT_FILTERS, SEARCH_FIELDS, customerName, getFilterState } from './customerColumns';

// Earlier versions cached pages of customer records here; never read again
const LEGACY_CACHE_KEY = 'customersPageCache';

const hasActiveFilter = (filters, searchTerm) => {
  // Arama en az 3 karakterle sayılır; varsayılan "aktif" durum filtresi sayılmaz
  const hasActiveSearch = searchTerm && searchTerm.trim().length >= 3;
  const hasActiveFilters = Object.keys(filters).some((key) => {
    if (key === 'Durum' && filters[key] === 'aktif') return false;
    return getFilterState(key, filters[key]) === 'applied';
  });
  return Boolean(hasActiveSearch || hasActiveFilters);
};

const filterCustomers = (customers, filters, searchTerm) => {
  let filtered = customers;

  // Önce arama (başlık, bilgi, vergi numarası) - en az 3 karakter
  if (searchTerm && searchTerm.trim().length >= 3) {
    const lowercasedSearch = searchTerm.toLowerCase();
    filtered = filtered.filter((customer) => SEARCH_FIELDS.some((field) => (
      customer[field] && customer[field].toString().toLowerCase().includes(lowercasedSearch)
    )));
  }

  // Sonra sütun filtreleri
  Object.keys(filters).forEach((key) => {
    const value = filters[key];
    if (!value) return;
    const type = CUSTOMER_COLUMNS[key]?.type;
    if (key === 'Durum') {
      // Durum her değerde uygulanır
      filtered = filtered.filter((customer) => customer[key]?.toLowerCase() === String(value).toLowerCase());
    } else if (type === 'select') {
      // Seçim filtreleri birebir eşleşir
      filtered = filtered.filter((customer) => customer[key] === value);
    } else if (type === 'date') {
      // Tarih filtreleri hemen uygulanır
      filtered = filtered.filter((customer) => typeof customer[key] === 'string' && customer[key].includes(value));
    } else {
      // Metin filtreleri en az 3 karakterde
      const filterValue = String(value);
      if (filterValue.trim().length >= 3) {
        filtered = filtered.filter((customer) => String(customer[key]).toLowerCase().includes(filterValue.toLowerCase()));
      }
    }
  });

  return filtered;
};

/**
 * Müşteri Yönetimi listesi. The whole Customers collection is read once;
 * search, column filters and paging then run in the browser. Rows per page
 * come from the table, which reports how many fit on screen.
 *
 * NOT: Firestore orderBy bilerek kullanılmıyor - verilen alanı içermeyen
 * dokümanları sonuçtan düşürüyor ve 'Başlık' alanı olmayan müşteriler
 * tablodan kayboluyordu. Sıralama istemci tarafında yapılır.
 */
const useCustomerDirectory = () => {
  const { t } = useTranslation();
  // Read inside the stable fetcher, so switching language doesn't refetch
  const tRef = useRef(t);
  tRef.current = t;
  const [allCustomers, setAllCustomers] = useState([]);
  const [statsData, setStatsData] = useState({ total: 0, active: 0, inactive: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchCustomers = useCallback(async () => {
    setIsLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'Customers'));
      const customersData = querySnapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          // Tabloda ve aramada tutarlı bir ad alanı olması için normalize edilir
          'Başlık': customerName(data),
        };
      });

      customersData.sort((a, b) => customerName(a).localeCompare(customerName(b), 'tr', { sensitivity: 'base' }));

      setAllCustomers(customersData);
      setStatsData({
        total: customersData.length,
        active: customersData.filter((c) => c['Durum']?.toLowerCase() === 'aktif').length,
        inactive: customersData.filter((c) => c['Durum']?.toLowerCase() === 'inaktif').length,
      });
      setHasLoaded(true);
      return customersData;
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error(tRef.current('customers.toast.loadError'));
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.removeItem(LEGACY_CACHE_KEY);
    } catch (e) {
      // Storage unavailable; nothing to clean up
    }
    fetchCustomers();
  }, [fetchCustomers]);

  // Boş yer tutucular (isEmpty) listede gösterilmez; sayımlarda kalır
  const listedCustomers = useMemo(() => allCustomers.filter((customer) => !customer.isEmpty), [allCustomers]);
  const filteredCustomers = useMemo(
    () => filterCustomers(listedCustomers, filters, searchTerm),
    [listedCustomers, filters, searchTerm]
  );
  const isFilterActive = hasActiveFilter(filters, searchTerm);
  const paging = usePagedList(filteredCustomers, { resetKey: `${JSON.stringify(filters)}|${searchTerm}` });

  const handleFilter = useCallback((column, value) => {
    setFilters((prev) => ({ ...prev, [column]: value }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({});
    setSearchTerm('');
  }, []);

  return {
    allCustomers,
    setAllCustomers,
    filteredCustomers,
    filters,
    handleFilter,
    clearFilters,
    searchTerm,
    setSearchTerm,
    isFilterActive,
    isLoading,
    hasLoaded,
    statsData,
    ...paging,
    refreshCustomers: fetchCustomers,
  };
};

export default useCustomerDirectory;
